import { useLocation } from 'wouter'
import { ChevronLeft, Newspaper, Loader2 } from 'lucide-react'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import NewsCard, { NewsArticle } from '@/components/NewsCard'
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll'

const EmptyState = () => {
  return (
    <div className="flex flex-col items-center justify-center flex-1 px-6 py-20">
      <div className="relative mb-8">
        <div className="w-24 h-24 bg-gradient-to-b from-[#EF4444] to-[#DC2626] rounded-2xl flex items-center justify-center">
          <Newspaper className="w-12 h-12 text-white" />
        </div>
      </div>

      <h2 className="text-[#ffffff] text-xl font-semibold mb-2">
        No News Articles
      </h2>
      <p className="text-[#BABABB] text-sm text-center max-w-xs">
        Stay tuned for the latest news and updates from Secrets Hideaway.
      </p>
    </div>
  )
}

const LoadingState = () => {
  return (
    <div className="flex flex-col items-center justify-center py-20">
      <Loader2 className="h-8 w-8 text-[#EF4444] animate-spin" />
      <p className="text-[#BABABB] text-sm mt-4">Loading news...</p>
    </div>
  )
}

const LoadingMore = () => {
  return (
    <div className="flex items-center justify-center py-6">
      <Loader2 className="h-5 w-5 text-[#EF4444] animate-spin" />
      <span className="text-[#BABABB] text-sm ml-2">Loading more...</span>
    </div>
  )
}

export default function NewsPage() {
  const [, navigate] = useLocation()

  const {
    items: articles,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    sentinelRef
  } = useInfiniteScroll<NewsArticle>({
    endpoint: '/v1/secrets_hideaway/news',
    limit: 12
  })

  const hasArticles = articles.length > 0

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-[#18181B] flex flex-col">
        <div className="max-w-5xl mx-auto w-full px-5 pb-8">
          {/* Header with back button */}
          <div
            role="banner"
            className="h-12 flex items-center justify-center text-white mb-4 pt-3 relative"
          >
            <button
              type="button"
              aria-label="Go back"
              onClick={() => navigate('/')}
              className="absolute left-0
                h-8 w-8
                flex items-center justify-center
                rounded-full
                bg-white/10 hover:bg-white/20
                focus:outline-none focus:ring-2 focus:ring-white/40"
            >
              <ChevronLeft className="h-[22px] w-[22px] text-[#ffffff]" />
            </button>

            <h1 className="text-base font-semibold tracking-wide">
              News
            </h1>
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <LoadingState />
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 px-6">
            <p className="text-[#EF4444] text-center">
              Failed to load news. Please try again later.
            </p>
          </div>
        ) : hasArticles ? (
          <div className="flex-1 max-w-5xl mx-auto w-full px-5 pb-8">
            {/* News Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {articles.map((article) => (
                <NewsCard key={article.id} article={article} />
              ))}
            </div>

            {/* Loading More Indicator */}
            {isLoadingMore && <LoadingMore />}

            {/* Sentinel for infinite scroll */}
            {hasMore && <div ref={sentinelRef} className="h-4" />}

            {/* End of list message */}
            {!hasMore && articles.length > 0 && (
              <p className="text-center text-[#BABABB] text-sm py-6">
                You've seen all news articles
              </p>
            )}
          </div>
        ) : (
          <EmptyState />
        )}
      </div>
    </ErrorBoundary>
  )
}
